import { RxReplicationState } from 'rxdb/plugins/replication'
import { Subject } from 'rxjs'

const DEFAULT_LAST_MODIFIED_FIELD = '_modified'
const DEFAULT_DELETED_FIELD = '_deleted'
const POSTGRES_DUPLICATE_KEY_ERROR_CODE = '23505'

function stripTransactionFields(doc) {
  return {
    id: doc.id,
    business_id: doc.business_id,
    branch_id: doc.branch_id,
    type: doc.type,
    transaction_type_tag: doc.transaction_type_tag,
    payment_account: doc.payment_account,
    account_code: doc.account_code,
    date: doc.date,
    created_by: doc.created_by,
    created_at: doc.created_at ?? null,
    updated_at: doc.updated_at ?? null,
    account_id: doc.account_id ?? null,
    source_id: doc.source_id ?? null,
    source_type: doc.source_type ?? null,
    lifecycle_state: doc.lifecycle_state || 'finalized',
    amount: doc.amount,
    display_name: doc.display_name,
    _deleted: doc._deleted,
    _modified: doc._modified,
  }
}

function stripExpenseFields(expense, txn = null) {
  return {
    business_id: txn?.business_id ?? expense.business_id ?? null,
    branch_id: txn?.branch_id ?? expense.branch_id ?? null,
    stock_entry_id: expense.stock_entry_id ?? null,
    category: expense.category,
    amount: expense.amount,
    description: expense.description ?? null,
    receipt_url: expense.receipt_url ?? null,
    created_by: expense.created_by ?? txn?.created_by ?? null,
    account_id: expense.account_id ?? null,
    date: expense.date ?? expense.created_at ?? txn?.date ?? null,
    created_at: expense.created_at ?? null,
    _modified: Date.now(),
    _deleted: false,
  }
}

function stripTransferFields(transfer) {
  return {
    id: transfer.id,
    business_id: transfer.business_id,
    branch_id: transfer.branch_id,
    from_account: transfer.from_account,
    to_account: transfer.to_account,
    amount: transfer.amount,
    transaction_cost: transfer.transaction_cost,
    date: transfer.date,
    note: transfer.note ?? null,
    created_by: transfer.created_by,
  }
}

class SupabaseReplication extends RxReplicationState {
  constructor(options) {
    const realtimeChanges = new Subject()
    const rawId = options.replicationIdentifier || options.collection?.name || ''
    const safeId = String(rawId).replace(/[^a-zA-Z0-9]/g, '')

    super(
      safeId,
      options.collection,
      options.deletedField || DEFAULT_DELETED_FIELD,
      options.pull && {
        ...options.pull,
        stream$: realtimeChanges,
        handler: (lastCheckpoint, batchSize) => this.pullHandler(lastCheckpoint, batchSize),
      },
      options.push && {
        ...options.push,
        batchSize: 1,
        handler: (rows) => this.pushHandler(rows),
      },
      typeof options.live === 'undefined' ? true : options.live,
      typeof options.retryTime === 'undefined' ? 5000 : options.retryTime,
      false,
    )

    this.options = options
    this.realtimeChanges = realtimeChanges
    this.table = options.table || options.collection.name
    this.primaryKey = options.primaryKey || options.collection.schema.primaryPath
    this.lastModifiedFieldName = options.pull?.lastModifiedField || DEFAULT_LAST_MODIFIED_FIELD

    if (
      options.pull &&
      (options.pull.realtimePostgresChanges || typeof options.pull.realtimePostgresChanges === 'undefined')
    ) {
      this.setupRealtimeChannel()
    }

    if (options.autoStart === undefined || options.autoStart) {
      this.start()
    }
  }

  setupRealtimeChannel() {
    if (this.realtimeChannel) return

    const channelName = `replication-${this.table}-${this.primaryKey}`
    this.realtimeChannel = this.options.supabaseClient
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: this.table,
        },
        () => this.realtimeChanges.next(Date.now()),
      )
      .subscribe((status) => {
        this.realtimeChannelReady = status === 'SUBSCRIBED'
      })
  }

  async pullHandler(lastCheckpoint, batchSize) {
    let query = this.options.pull?.queryBuilder
      ? await this.options.pull.queryBuilder(lastCheckpoint, batchSize)
      : this.options.supabaseClient
        .from(this.table)
        .select('*')

    if (!this.options.pull?.queryBuilder && lastCheckpoint?.modified) {
      const lastModified = JSON.stringify(lastCheckpoint.modified)
      const lastPrimaryKey = JSON.stringify(lastCheckpoint.primaryKeyValue)
      const isNewer = `${this.lastModifiedFieldName}.gt.${lastModified}`
      const isSameAge = `${this.lastModifiedFieldName}.eq.${lastModified}`

      query = query.or(`${isNewer},and(${isSameAge},${this.primaryKey}.gt.${lastPrimaryKey})`)
    }

    if (!this.options.pull?.queryBuilder) {
      query = query.order(this.lastModifiedFieldName).order(this.primaryKey).limit(batchSize)
    }

    const { data, error } = await query
    if (error) throw error

    if (!data || data.length === 0) {
      return {
        checkpoint: lastCheckpoint,
        documents: [],
      }
    }

    return {
      checkpoint: this.rowToCheckpoint(data[data.length - 1]),
      documents: data.map((row) => this.rowToRxDoc(row)),
    }
  }

  async pushHandler(rows) {
    if (!Array.isArray(rows) || rows.length === 0) return []

    const results = []

    for (const row of rows) {
      if (row.assumedMasterState) {
        results.push(...(await this.handleUpdate(row)))
      } else {
        results.push(...(await this.handleInsertion(row.newDocumentState)))
      }
    }

    return results
  }

  async handleInsertion(doc) {
    if (this.options.push?.customInsertHandler) {
      return this.options.push.customInsertHandler(doc)
    }

    const txn = stripTransactionFields(doc)
    const { data: txnRow, error: txnError } = await this.options.supabaseClient
      .from(this.table)
      .insert(txn)
      .select()
      .single()

    if (txnError) {
      if (txnError.code === POSTGRES_DUPLICATE_KEY_ERROR_CODE) {
        return [await this.fetchByPrimaryKey(doc[this.primaryKey])]
      }

      throw txnError
    }

    if (doc.type === 'sale' && Array.isArray(doc.sale_items) && doc.sale_items.length > 0) {
      const items = doc.sale_items.map((item) => ({
        sale_id: txnRow.id,
        product_id: item.product_id,
        quantity: item.quantity,
        unit_price: item.unit_price,
        total_amount: item.total_amount,
        vat_applied: item.vat_applied ?? 0,
        etims_receipt_no: item.etims_receipt_no ?? null,
      }))

      const { error: itemsError } = await this.options.supabaseClient
        .from('sale_items')
        .insert(items)

      if (itemsError) throw itemsError
    } else if (doc.type === 'expense' && doc.expense) {
      const { data: expenseRow, error: expenseError } = await this.options.supabaseClient
        .from('expenses')
        .insert(stripExpenseFields(doc.expense, txnRow))
        .select()
        .single()

      if (expenseError) throw expenseError

      const { error: txnLinkError } = await this.options.supabaseClient
        .from(this.table)
        .update({ source_id: expenseRow.id, source_type: 'expense' })
        .eq('id', txnRow.id)

      if (txnLinkError) throw txnLinkError
    } else if (doc.type === 'transfer' && doc.transfer) {
      const { error: transferError } = await this.options.supabaseClient
        .from('transfers')
        .insert(stripTransferFields(doc.transfer))

      if (transferError) throw transferError

      if (doc.costExpense?.transaction && doc.costExpense?.expense) {
        const costTxn = stripTransactionFields(doc.costExpense.transaction)

        const { data: costTxnRow, error: costTxnError } = await this.options.supabaseClient
          .from(this.table)
          .insert(costTxn)
          .select()
          .single()

        if (costTxnError) throw costTxnError

        const { data: costExpenseRow, error: costExpenseError } = await this.options.supabaseClient
          .from('expenses')
          .insert(stripExpenseFields(doc.costExpense.expense, costTxnRow))
          .select()
          .single()

        if (costExpenseError) throw costExpenseError

        const { error: costTxnLinkErr } = await this.options.supabaseClient
          .from(this.table)
          .update({ source_id: costExpenseRow.id, source_type: 'expense' })
          .eq('id', costTxnRow.id)

        if (costTxnLinkErr) throw costTxnLinkErr
      }
    }

    return []
  }

  async handleUpdate(row) {
    if (this.options.push?.customUpdateHandler) {
      return this.options.push.customUpdateHandler(row)
    }

    const updateHandler = this.options.push?.updateHandler || this.defaultUpdateHandler.bind(this)

    if (await updateHandler(row)) {
      return []
    }

    return [await this.fetchByPrimaryKey(row.newDocumentState[this.primaryKey])]
  }

  async defaultUpdateHandler(row) {
    let query = this.options.supabaseClient
      .from(this.table)
      .update(row.newDocumentState, { count: 'exact' })

    Object.entries(row.assumedMasterState).forEach(([field, value]) => {
      const type = typeof value

      if (type === 'string' || type === 'number') {
        query = query.eq(field, value)
      } else if (type === 'boolean' || value === null) {
        query = query.is(field, value)
      } else {
        throw new Error(`replicateSupabase: Unsupported field of type ${type}`)
      }
    })

    const { error, count } = await query
    if (error) throw error

    return count === 1
  }

  async fetchByPrimaryKey(primaryKeyValue) {
    const { data, error } = await this.options.supabaseClient
      .from(this.table)
      .select()
      .eq(this.primaryKey, primaryKeyValue)
      .limit(1)

    if (error) throw error

    if (data.length !== 1) {
      throw new Error('No row with given primary key')
    }

    return this.rowToRxDoc(data[0])
  }

  rowToRxDoc(row) {
    return {
      ...row,
      _deleted: row._deleted ?? false,
    }
  }

  rowToCheckpoint(row) {
    return {
      modified: row[this.lastModifiedFieldName],
      primaryKeyValue: row[this.primaryKey],
    }
  }
}

export { SupabaseReplication }
